import { useCallback, useEffect, useRef, useState } from 'react';

import client from '../api/client';
import { connectChatSocket } from '../api/chatSocket';

// One chat conversation + its messages, for the Phase 8 staff console
// (/api/admin, requireAdmin) — no owner check, staff can view/reply to any
// customer's conversation.
// GET    /admin/chatbot/conversations/:id                -> { conversation, messages }
// PUT    /admin/chatbot/conversations/:id/mode  { mode }  -> updated conversation
// POST   /admin/chatbot/conversations/:id/messages { content } -> { conversation, message }
// DELETE /admin/chatbot/conversations/:id                -> { deleted, id }
// (useAdminChatConversations also has its own delete, taking an id directly —
// used by the conversation list's per-row delete, which has no single id of
// its own to scope this hook to. Two call sites, kept separate rather than
// forcing the list page to mount just for one action, or this page to mount
// a whole paginated list fetch just to reach a delete call.)
export default function useAdminChatConversation(id) {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [aiStats, setAiStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(true);
  const [deleted, setDeleted] = useState(false);
  const socketRef = useRef(null);
  const idRef = useRef(id);
  const reloadRef = useRef(() => {});

  const reload = useCallback(async () => {
    if (!id) return;
    const { data } = await client.get(`/admin/chatbot/conversations/${id}`);
    setConversation(data.conversation);
    setMessages(data.messages);
    setAiStats(data.aiStats);
  }, [id]);

  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);

  // Realtime delivery (Phase 8) — a rep with a conversation open needs to
  // see the customer's next message (or the bot's, before a rep takes over)
  // without manually hitting Refresh. Kept independent of `id` (via the refs
  // above) so switching the selected conversation doesn't tear down and
  // reconnect the socket.
  useEffect(() => {
    const socket = connectChatSocket();
    socketRef.current = socket;

    // A reconnect (network flake, backgrounded tab) gets a new underlying
    // connection server-side, so the room join below doesn't survive it —
    // 'connect' fires again on every reconnect, not just the first one, so
    // rejoin the open conversation and reload it to pick up anything sent
    // while disconnected (chat_bot_implementation.md Phase 10).
    let hasConnectedOnce = false;
    const onConnect = () => {
      setConnected(true);
      if (!hasConnectedOnce) {
        hasConnectedOnce = true;
        return;
      }
      const currentId = idRef.current;
      if (!currentId) return;
      socket.emit('join', currentId);
      Promise.resolve(reloadRef.current()).catch(() => {
        // gone while disconnected (expired/deleted) — nothing more to sync
      });
    };
    const onDisconnect = () => setConnected(false);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  // Backstop for missed live updates — the socket reconnect handling above
  // depends on this tab's 'connect' event actually firing again, but a
  // backgrounded/inactive tab gets throttled by the browser (delayed timers,
  // paused reconnection backoff), so a drop that happens while a rep is
  // looking at a different tab can go unnoticed indefinitely. Whenever this
  // tab regains focus, force a fresh fetch rather than trusting the socket
  // to have caught everything — cheap and idempotent either way.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible' || !idRef.current) return;
      Promise.resolve(reloadRef.current()).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    idRef.current = id;
    setDeleted(false);
    const socket = socketRef.current;
    if (!socket || !id) return undefined;

    const onMessage = (message) => {
      setMessages((prev) => (prev.some((m) => m._id === message._id) ? prev : [...prev, message]));
    };
    // conversation-level changes (mode/status/handoffRequested) pushed by
    // adminSetMode and requestHumanHandoff — without this, a rep with the
    // conversation open only sees them after a manual Refresh.
    const onConversation = (updated) => setConversation(updated);
    // the record itself is gone (customer or another rep deleted it) —
    // nothing left to reload; the page has to bail out instead, which it
    // can't do from inside this hook, so just flag it via `deleted`.
    const onDeleted = () => setDeleted(true);
    socket.on('message', onMessage);
    socket.on('conversation', onConversation);
    socket.on('conversationDeleted', onDeleted);
    socket.emit('join', id);

    return () => {
      socket.off('message', onMessage);
      socket.off('conversation', onConversation);
      socket.off('conversationDeleted', onDeleted);
      socket.emit('leave', id);
    };
  }, [id]);

  useEffect(() => {
    if (!id) {
      setConversation(null);
      setMessages([]);
      setAiStats(null);
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError(null);
    client
      .get(`/admin/chatbot/conversations/${id}`)
      .then(({ data }) => {
        if (active) {
          setConversation(data.conversation);
          setMessages(data.messages);
          setAiStats(data.aiStats);
        }
      })
      .catch((err) => {
        if (active) setError(err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  const setMode = useCallback(
    async (mode) => {
      const { data } = await client.put(`/admin/chatbot/conversations/${id}/mode`, { mode });
      setConversation(data);
      return data;
    },
    [id]
  );

  const sendMessage = useCallback(
    async (content) => {
      const { data } = await client.post(`/admin/chatbot/conversations/${id}/messages`, { content });
      setConversation(data.conversation);
      // deduped against the socket's own delivery of this same reply (onMessage
      // above) — the two can arrive in either order.
      setMessages((prev) => (prev.some((m) => m._id === data.message._id) ? prev : [...prev, data.message]));
      return data;
    },
    [id]
  );

  const deleteConversation = useCallback(async () => {
    const { data } = await client.delete(`/admin/chatbot/conversations/${id}`);
    return data;
  }, [id]);

  return { conversation, messages, aiStats, loading, error, connected, deleted, reload, setMode, sendMessage, deleteConversation };
}
